import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSidebar } from '../contexts/SidebarContext';
import { newMissionHref } from '../lib/bureau/newMission';
import { cockpitHref } from '../lib/workQueue/cockpitView';
import { useNewMission } from './useNewMission';

/**
 * Asking Otto from a tool: the only door since the chat panel left (Gilles, 12/10/2026: « tout passe
 * par le Bureau, plus besoin du chat Otto à droite »). Asking Otto is giving him a mission:
 * - `prefill`: a request one rereads opens « Nouvelle mission » with it written (and its client),
 *   where « Confier à Otto » starts it — what the chat's « relis puis envoie » was;
 * - `start`: a request the tool writes whole (a meeting report, a LinkedIn planning) becomes the
 *   mission at once, through the same door as « Confier à Otto » (`useNewMission`), and opens in the
 *   Bureau. The text goes in the request, not the URL. From an editor whose edits are not saved,
 *   `stay` creates it without leaving the page: `created` is then the mission's link to show.
 * Going to the Bureau closes the CRM side sheet the gesture may come from: it would stay open over
 * the new page (`GlobalSidebars` outlives the route).
 */
export function useAskOtto() {
  const navigate = useNavigate();
  const sidebar = useSidebar({ optional: true });
  const { create, pending, error } = useNewMission();
  const [created, setCreated] = useState<string | null>(null);

  const prefill = useCallback(
    (text: string, client?: string | null) => {
      sidebar?.closeSidebar();
      navigate(newMissionHref({ text, client }));
    },
    [navigate, sidebar],
  );

  const start = useCallback(
    async (text: string, account?: { id: string; name: string } | null, opts?: { stay?: boolean }): Promise<boolean> => {
      setCreated(null);
      const mission = await create({ text, account: account ?? null, procedure: null, driveFileIds: [] });
      if (!mission) return false;
      const href = cockpitHref('attente', { kind: 'item', source: mission.source, sourceId: mission.sourceId });
      if (opts?.stay) {
        setCreated(href);
      } else {
        sidebar?.closeSidebar();
        navigate(href);
      }
      return true;
    },
    [create, navigate, sidebar],
  );

  return { prefill, start, pending, error, created };
}
